// 透過化の純ロジック（画像コーデック非依存）。Node 版（pngjs/jpeg-js）とブラウザ版（Canvas）の
// 両方から import され、アルゴリズムの単一実装を保証する（2026-07-11: シム側の独自実装が
// グラデーションを伝ってキャラ本体まで透過する不具合を出したため共通化）。

export function hasMeaningfulAlpha({ data }) {
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 250) return true;
    }
    return false;
}

/** 外周画素の中央値から背景色を推定する（純白とは限らない: grok は紙風のオフホワイト）。 */
function estimateBackgroundColor({ width, height, data }) {
    const rs = [], gs = [], bs = [];
    const push = (x, y) => {
        const i = (y * width + x) * 4;
        rs.push(data[i]); gs.push(data[i + 1]); bs.push(data[i + 2]);
    };
    for (let x = 0; x < width; x++) { push(x, 0); push(x, height - 1); }
    for (let y = 0; y < height; y++) { push(0, y); push(width - 1, y); }
    const median = (a) => a.sort((m, n) => m - n)[Math.floor(a.length / 2)];
    return [median(rs), median(gs), median(bs)];
}

const colorDistTo = (data, i, [br, bg, bb]) =>
    Math.max(Math.abs(data[i] - br), Math.abs(data[i + 1] - bg), Math.abs(data[i + 2] - bb));

const WHITE = [255, 255, 255];

// 背景判定は「外周の推定背景色」と「純白」の近い方（grok の紙風オフホワイト背景では、
// キャラ縁の純白ハロー（背景よりさらに明るい）が推定背景色±tolerance から漏れて残るため）
const colorDist = (data, i, background) =>
    Math.min(colorDistTo(data, i, background), colorDistTo(data, i, WHITE));

/**
 * 外周から連結した背景を透過化する（破壊的に data の alpha を書き換える）。
 * @returns {{ removedRatio: number, background: number[] }} 透過化した画素の割合と推定背景色
 */
export function removeConnectedBackground(rgba, { tolerance = 32 } = {}) {
    const { width, height, data } = rgba;
    const background = estimateBackgroundColor(rgba);
    const removed = new Uint8Array(width * height);
    const queue = [];
    const trySeed = (x, y) => {
        const p = y * width + x;
        if (!removed[p] && colorDist(data, p * 4, background) <= tolerance) { removed[p] = 1; queue.push(p); }
    };
    for (let x = 0; x < width; x++) { trySeed(x, 0); trySeed(x, height - 1); }
    for (let y = 0; y < height; y++) { trySeed(0, y); trySeed(width - 1, y); }
    // BFS（4 近傍）: 背景色 ±tolerance の連結領域だけを広げる
    while (queue.length > 0) {
        const p = queue.pop();
        const x = p % width;
        const y = (p - x) / width;
        for (const [nx, ny] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]]) {
            if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
            const np = ny * width + nx;
            if (!removed[np] && colorDist(data, np * 4, background) <= tolerance) { removed[np] = 1; queue.push(np); }
        }
    }
    // 透過化＋境界フェザリング（透過に隣接する画素は背景色との距離に応じて半透明化＝白フチ軽減）
    let removedCount = 0;
    for (let p = 0; p < removed.length; p++) {
        if (removed[p]) { data[p * 4 + 3] = 0; removedCount++; }
    }
    featherRemovedEdges(rgba, removed, background, tolerance);
    return { removedRatio: removedCount / removed.length, background };
}

function featherRemovedEdges({ width, height, data }, removed, background, tolerance) {
    for (let p = 0; p < removed.length; p++) {
        if (removed[p]) continue;
        const x = p % width;
        const y = (p - x) / width;
        const nearRemoved =
            (x > 0 && removed[p - 1]) || (x < width - 1 && removed[p + 1]) ||
            (y > 0 && removed[p - width]) || (y < height - 1 && removed[p + width]);
        if (!nearRemoved) continue;
        const dist = colorDist(data, p * 4, background);
        if (dist < tolerance * 2) {
            data[p * 4 + 3] = Math.min(data[p * 4 + 3], Math.round((dist / (tolerance * 2)) * 255));
        }
    }
}

/**
 * 立ち絵の足元アーティファクトを追加透過する（開始画面立ち絵用・2026-07-19）。
 * removeConnectedBackground は「外周と連結した背景」しか消せないため、
 *   (a) 脚の間に閉じ込められた背景色ポケット（白い空間）
 *   (b) 足元に描かれた低彩度の床影（ポケットと外周の間を塞いでいる本体）
 * が不透明のまま残る。本関数は既存の透明画素（alpha<128）を「外側」として、
 * そこから連結する (a)(b) だけを追加でフラッドフィル透過する。
 * - 床影判定は「低彩度かつ明るめ」の画素だけ・不透明領域の下端 footRegionRatio 内に限定
 *   （黒/茶ブーツ・銀髪・白い服の内側など、キャラ本体を巻き込まないための制約）
 * - 生成直後（removeConnectedBackground 直後）にも、保存済みの透過 PNG にも適用できる
 *   （ブラウザ Canvas 経由では alpha=0 画素の RGB が失われるため、背景色は不透明外周から推定できない
 *    場合に純白へフォールバックする）
 * @returns {{ removedRatio: number, background: number[] }} 追加透過した画素の割合と背景推定色
 */
export function clearPortraitFootArtifacts(rgba, {
    tolerance = 32,
    footRegionRatio = 0.24,
    shadowChromaMax = 26,
    shadowMinBrightness = 96,
} = {}) {
    const { width, height, data } = rgba;
    const outside = new Uint8Array(width * height);
    let minY = height, maxY = -1;
    for (let p = 0; p < outside.length; p++) {
        if (data[p * 4 + 3] < 128) {
            outside[p] = 1;
        } else {
            const y = Math.floor(p / width);
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
        }
    }
    if (maxY < 0) return { removedRatio: 0, background: WHITE };
    // 背景色は「透明画素の RGB」が信頼できる場合のみ外周中央値を使う（Canvas 経由の保存画像では
    // alpha=0 の RGB が黒化するため、黒に近い推定値は捨てて純白基準にする）
    const estimated = estimateBackgroundColor(rgba);
    const background = Math.max(...estimated) >= 160 ? estimated : WHITE;
    const footY = maxY - Math.max(1, Math.round((maxY - minY + 1) * footRegionRatio));
    const chromaOf = (i) => Math.max(data[i], data[i + 1], data[i + 2]) - Math.min(data[i], data[i + 1], data[i + 2]);
    const brightnessOf = (i) => Math.max(data[i], data[i + 1], data[i + 2]);
    const isClearable = (p) => {
        const i = p * 4;
        if (colorDist(data, i, background) <= tolerance) return true; // 閉じ込められた背景色ポケット
        const y = Math.floor(p / width);
        return y >= footY && chromaOf(i) <= shadowChromaMax && brightnessOf(i) >= shadowMinBrightness; // 足元の床影
    };
    // 既存の透明領域に接する画素からシードして BFS（4 近傍）
    const removed = new Uint8Array(width * height);
    const queue = [];
    for (let p = 0; p < outside.length; p++) {
        if (!outside[p]) continue;
        const x = p % width;
        const y = (p - x) / width;
        for (const [nx, ny] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]]) {
            if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
            const np = ny * width + nx;
            if (!outside[np] && !removed[np] && isClearable(np)) { removed[np] = 1; queue.push(np); }
        }
    }
    while (queue.length > 0) {
        const p = queue.pop();
        const x = p % width;
        const y = (p - x) / width;
        for (const [nx, ny] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]]) {
            if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
            const np = ny * width + nx;
            if (!outside[np] && !removed[np] && isClearable(np)) { removed[np] = 1; queue.push(np); }
        }
    }
    let removedCount = 0;
    for (let p = 0; p < removed.length; p++) {
        if (removed[p]) { data[p * 4 + 3] = 0; removedCount++; }
    }
    featherRemovedEdges(rgba, removed, background, tolerance);
    return { removedRatio: removedCount / (width * height), background };
}
